import type { RunAction } from './AddClient.tsx';
import { RowDrawer, type RowKind } from './admin/ClientDrawers.tsx';
import type { Tenant } from './api.ts';

/**
 * The row-level manage vocabulary the Clients screen's tables share.
 *
 * It sits beside `Portfolio` rather than inside it because both tables on that
 * screen need it — the live one, which is the assessment table, and the
 * archived one, which is four columns of identity — and because Portfolio was
 * within a hundred lines of the 800-line ceiling without it. What lives here is
 * everything a *row* offers: the connection chip's wording, the Manage button,
 * the drawer row it opens into, and the archived table in full.
 *
 * The drawers themselves are untouched in `admin/ClientDrawers.tsx`. They were
 * split out of the old Client management screen for the same reason and they
 * change for their own reasons; this file only decides which of them a row may
 * open.
 */

/** The `.conn` variant each connection state renders as. One copy, two tables. */
export const CONNECTION: Record<Tenant['connectionState'], { text: string; chip: string }> = {
  connected: { text: 'Connected', chip: 'conn-connected' },
  pending_consent: { text: 'Awaiting consent', chip: 'conn-pending' },
  consent_revoked: { text: 'Consent revoked', chip: 'conn-revoked' },
  insufficient_permissions: { text: 'Insufficient permissions', chip: 'conn-insufficient' },
  error: { text: 'Connection error', chip: 'conn-error' },
};

/**
 * What is open in a row's drawer: the list of actions, or one of them.
 *
 * The three action buttons used to sit in the Manage cell itself, and the
 * column they needed could not fit — at the console's 1280px floor the content
 * column is about 966px, and the table wanted 1180, so "Reconnect" rendered as
 * "Reconnec". One button per row fits; the choice moved one step later, into
 * the drawer that was already opening underneath.
 *
 * A drawer rather than a menu on purpose. A popover would be a new interaction
 * pattern needing click-outside handling, a focus trap and its own keyboard
 * contract; the drawer row exists, is in the table's tab order already, and
 * costs a `'menu'` member on a union to reuse.
 */
export type DrawerKind = RowKind | 'menu';

/** Which row has a drawer open, and what is in it. At most one on the screen. */
export interface Pending {
  readonly tenantId: string;
  readonly kind: DrawerKind;
}

/**
 * What the signed-in person may do to a client.
 *
 * Two capabilities rather than one, because the server asks for two: creating
 * and correcting a client is `tenants.write`, and everything that changes
 * whether a client exists at all — reconnect, archive, unarchive, purge — is
 * `deployment.manage`. The screen is visible to every role now that it is the
 * only client list, so this is what stands between a viewer and a mutation on
 * the client side. It prevents nothing on the server, where every one of these
 * routes is authorised again.
 */
export interface Capabilities {
  readonly canEdit: boolean;
  readonly canManage: boolean;
}

/** Whether a row offers anything at all, and so whether a Manage column exists. */
export const hasRowActions = ({ canEdit, canManage }: Capabilities): boolean =>
  canEdit || canManage;

interface ManageButtonProps {
  readonly tenant: Tenant;
  /** What this row has open, or null. Any open drawer makes the button a close. */
  readonly open: DrawerKind | null;
  readonly busy: boolean;
  readonly onOpen: (tenantId: string, kind: DrawerKind | null) => void;
}

export const ManageButton = ({ tenant, open, busy, onOpen }: ManageButtonProps) => (
  <button
    type="button"
    className="btn btn-quiet btn-sm"
    aria-expanded={open !== null}
    aria-label={`Manage ${tenant.displayName}`}
    disabled={busy}
    onClick={() => onOpen(tenant.id, open === null ? 'menu' : null)}
  >
    Manage
  </button>
);

interface ActionMenuProps {
  readonly tenant: Tenant;
  /** Archived rows offer the two lifecycle verbs the live ones cannot, and vice versa. */
  readonly archived: boolean;
  readonly capabilities: Capabilities;
  readonly busy: boolean;
  readonly onChoose: (kind: RowKind) => void;
  readonly onCancel: () => void;
}

/**
 * The first thing the drawer shows: which verbs this person has on this row.
 * Choosing one replaces this with that action's own form, which carries its own
 * confirmation — so nothing here can change anything by itself.
 */
const ActionMenu = ({
  tenant,
  archived,
  capabilities,
  busy,
  onChoose,
  onCancel,
}: ActionMenuProps) => {
  const { canEdit, canManage } = capabilities;

  return (
    <div className="admin-drawer">
      <p className="admin-drawer-text">
        Choose what to do with <strong>{tenant.displayName}</strong>. Nothing happens until you
        confirm it in the next step.
      </p>
      <div className="toolbar admin-row-actions">
        {/* Edit is rendered inert with its reason rather than hidden, because
            the person seeing it holds `deployment.manage` and not
            `tenants.write` — they can archive this client but not rename it,
            which is surprising enough to be worth saying out loud. A viewer
            holds neither, so the Manage column is absent for them entirely and
            there is nothing to explain. */}
        <MenuButton
          label="Edit"
          busy={busy}
          reason={canEdit ? null : 'Editing a client needs the tenants.write capability.'}
          onChoose={() => onChoose('edit')}
        />
        {canManage && !archived && (
          <MenuButton label="Reconnect" busy={busy} reason={null} onChoose={() => onChoose('reconnect')} />
        )}
        {canManage && !archived && (
          <MenuButton label="Archive" busy={busy} reason={null} onChoose={() => onChoose('archive')} />
        )}
        {canManage && archived && (
          <MenuButton label="Unarchive" busy={busy} reason={null} onChoose={() => onChoose('unarchive')} />
        )}
        {canManage && archived && (
          <MenuButton label="Purge" busy={busy} reason={null} onChoose={() => onChoose('purge')} />
        )}
        <button type="button" className="btn btn-quiet btn-sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

interface MenuButtonProps {
  readonly label: string;
  readonly busy: boolean;
  readonly reason: string | null;
  readonly onChoose: () => void;
}

const MenuButton = ({ label, busy, reason, onChoose }: MenuButtonProps) => (
  <button
    type="button"
    className="btn btn-quiet btn-sm"
    disabled={busy || reason !== null}
    title={reason ?? undefined}
    onClick={onChoose}
  >
    {label}
  </button>
);

interface DrawerRowProps {
  readonly kind: DrawerKind;
  readonly tenant: Tenant;
  readonly archived: boolean;
  readonly capabilities: Capabilities;
  readonly busy: boolean;
  /** How many columns the table has, so the drawer spans all of them. */
  readonly columns: number;
  readonly onOpen: (tenantId: string, kind: DrawerKind | null) => void;
  readonly onRun: RunAction;
}

/** The drawer opens inside the table, in the row below the one it belongs to. */
export const DrawerRow = ({
  kind,
  tenant,
  archived,
  capabilities,
  busy,
  columns,
  onOpen,
  onRun,
}: DrawerRowProps) => (
  <tr className="admin-drawer-row">
    <td colSpan={columns}>
      {kind === 'menu' ? (
        <ActionMenu
          tenant={tenant}
          archived={archived}
          capabilities={capabilities}
          busy={busy}
          onChoose={(chosen) => onOpen(tenant.id, chosen)}
          onCancel={() => onOpen(tenant.id, null)}
        />
      ) : (
        <RowDrawer
          kind={kind}
          tenant={tenant}
          busy={busy}
          onCancel={() => onOpen(tenant.id, null)}
          onRun={onRun}
        />
      )}
    </td>
  </tr>
);

interface ArchivedClientsProps {
  readonly tenants: readonly Tenant[];
  readonly capabilities: Capabilities;
  readonly busy: boolean;
  readonly pending: Pending | null;
  readonly onOpen: (tenantId: string, kind: DrawerKind | null) => void;
  readonly onRun: RunAction;
}

/**
 * The archived view: identity, connection, contact, and what may be done about
 * it. Deliberately not the assessment table — an archived client has no current
 * assessment, and drawing the attention column for one would rank a client
 * nobody is watching against clients somebody is.
 */
export const ArchivedClients = ({
  tenants,
  capabilities,
  busy,
  pending,
  onOpen,
  onRun,
}: ArchivedClientsProps) => {
  const manageable = hasRowActions(capabilities);
  const columns = 3 + (manageable ? 1 : 0);

  return (
    <section className="admin-section cl-archived" aria-label="Archived clients">
      <div className="admin-section-head">
        <h2>Archived clients</h2>
        <span className="admin-count num">{tenants.length}</span>
      </div>
      <div className="tbl-wrap admin-wrap">
        <table className="tbl admin-table">
          <thead>
            <tr>
              <th scope="col">Client</th>
              <th scope="col">Connection</th>
              <th scope="col">Contact</th>
              {manageable && <th scope="col">Manage</th>}
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <ArchivedRow
                key={tenant.id}
                tenant={tenant}
                capabilities={capabilities}
                busy={busy}
                columns={columns}
                open={pending !== null && pending.tenantId === tenant.id ? pending.kind : null}
                onOpen={onOpen}
                onRun={onRun}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

interface ArchivedRowProps {
  readonly tenant: Tenant;
  readonly capabilities: Capabilities;
  readonly busy: boolean;
  readonly columns: number;
  readonly open: DrawerKind | null;
  readonly onOpen: (tenantId: string, kind: DrawerKind | null) => void;
  readonly onRun: RunAction;
}

const ArchivedRow = ({
  tenant,
  capabilities,
  busy,
  columns,
  open,
  onOpen,
  onRun,
}: ArchivedRowProps) => {
  const connection = CONNECTION[tenant.connectionState];

  return (
    <>
      <tr className="dimmed">
        <th scope="row" className="admin-client">
          <span className="admin-name">
            {tenant.displayName}
            <span className="admin-archived-tag">Archived</span>
          </span>
          <span className="mono admin-id">{tenant.entraTenantId}</span>
        </th>
        <td>
          <span className={`conn ${connection.chip}`}>{connection.text}</span>
        </td>
        <td>
          {tenant.contactName !== null || tenant.contactEmail !== null ? (
            <span className="admin-contact">
              {tenant.contactName !== null && (
                <span className="admin-contact-name">{tenant.contactName}</span>
              )}
              {tenant.contactEmail !== null && (
                <span className="mono admin-contact-email">{tenant.contactEmail}</span>
              )}
            </span>
          ) : (
            <span className="note">—</span>
          )}
        </td>
        {hasRowActions(capabilities) && (
          <td>
            <ManageButton tenant={tenant} open={open} busy={busy} onOpen={onOpen} />
          </td>
        )}
      </tr>

      {open !== null && (
        <DrawerRow
          kind={open}
          tenant={tenant}
          archived
          capabilities={capabilities}
          busy={busy}
          columns={columns}
          onOpen={onOpen}
          onRun={onRun}
        />
      )}
    </>
  );
};
